import {
  Alert,
  Box,
  Container,
  Stack,
  Typography
} from '@customink/pigment-react';
import {zodResolver} from '@hookform/resolvers/zod';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import ConfirmationDialog from './components/ConfirmationDialog';
import ImagePreview from './components/ImagePreview';
import StyleForm from './components/StyleForm';
import useAlgoliaStyleMutation from './hooks/useAlgoliaStyleMutation';
import usePreviewUrl from './hooks/usePreviewUrl';
import {algoliaStyleSchema} from './schemas/algoliaStyleSchema';
import getInitialFormValues from './utils/getInitialFormValues';

export default function AlgoliaStyleAttributesEditor({
  backUrl,
  categoryImages,
  colors,
  endpointUrl,
  imageConfig,
  initialUseLabImage,
  primaryCategoryId,
  styleCardImageUrl,
  styleId
}) {
  const initialPreviewLoaded = useRef(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const {
    isError: isStyleSubmitError,
    isLoading,
    isSuccess,
    mutate: submitStyle
  } = useAlgoliaStyleMutation(endpointUrl);

  const initialValues = useMemo(
    () =>
      getInitialFormValues(
        primaryCategoryId,
        initialUseLabImage,
        imageConfig,
        categoryImages
      ),
    [primaryCategoryId, initialUseLabImage, imageConfig, categoryImages]
  );

  // React Hook Form setup with Zod validation
  const form = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: zodResolver(algoliaStyleSchema)
  });

  const {
    formState: {isValid, isValidating},
    getValues,
    handleSubmit
  } = form;

  const {previewUrl, refreshPreview} = usePreviewUrl({
    categoryImages,
    colors,
    getValues,
    primaryCategoryId,
    styleCardImageUrl
  });

  useEffect(() => {
    if (!initialPreviewLoaded.current) {
      initialPreviewLoaded.current = true;
      refreshPreview();
    }
  }, [refreshPreview]);

  useEffect(() => {
    if (isValid && !isValidating) {
      refreshPreview();
    }
  }, [isValid, refreshPreview, isValidating]);

  const handleBack = useCallback(() => {
    if (backUrl) {
      window.location.href = backUrl;
    }
  }, [backUrl]);

  const handleSubmitClick = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    setConfirmDialogOpen(false);
    await handleSubmit(submitStyle)();
  }, [handleSubmit, submitStyle]);

  const handleCancelSubmit = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  return (
    <Container maxWidth="xl">
      <FormProvider {...form}>
        <Box sx={{py: 4}}>
          {/* Header */}
          <Box sx={{mb: 4}}>
            <Typography variant="h4" gutterBottom>
              Edit Algolia Style Attributes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure image attributes for style {styleId} in the Algolia
              search index.
            </Typography>
          </Box>

          {isStyleSubmitError && (
            <Alert severity="error" sx={{mb: 2}}>
              Failed to update Algolia attributes. Please try again. If the
              error persists, contact engineering support.
            </Alert>
          )}

          {/* Main Content */}
          <Stack
            direction={{md: 'row', xs: 'column'}}
            spacing={4}
            alignItems="flex-start">
            {/* Form Section */}

            <StyleForm
              onBack={handleBack}
              styleId={styleId}
              success={isSuccess}
              colors={colors}
              isLoading={isLoading}
            />

            {/* Preview Section */}
            <ImagePreview
              previewUrl={previewUrl}
              onSubmit={handleSubmitClick}
              onBack={handleBack}
              isLoading={isLoading}
              categoryImages={categoryImages}
              colors={colors}
              primaryCategoryId={primaryCategoryId}
              styleCardImageUrl={styleCardImageUrl}
            />
          </Stack>

          {/* Confirmation Dialog */}
          <ConfirmationDialog
            open={confirmDialogOpen}
            previewUrl={previewUrl || styleCardImageUrl}
            onConfirm={handleConfirmSubmit}
            onCancel={handleCancelSubmit}
            categoryImages={categoryImages}
            colors={colors}
            primaryCategoryId={primaryCategoryId}
            styleCardImageUrl={styleCardImageUrl}
          />
        </Box>
      </FormProvider>
    </Container>
  );
}
